import type { ErrorEvent, EventHint } from '@sentry/react'
import * as Sentry from '@sentry/react'

interface SentryConfig {
	dsn: string | undefined
	environment: string
	appName: 'admin' | 'client'
	release?: string
}

export function initSentry({ dsn, environment, appName, release }: SentryConfig) {
	if (!dsn) {
		if (environment !== 'local') {
			console.warn('Sentry DSN not configured')
		}
		return
	}

	Sentry.init({
		dsn,
		environment,
		release: release || `${appName}@${import.meta.env.VITE_GIT_COMMIT_SHA || 'dev'}`,
		integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
		// Tag all events with app name for filtering in single project
		initialScope: {
			tags: { app: appName },
		},
		// Performance Monitoring
		tracesSampleRate: environment === 'prod' ? 0.1 : 1.0,
		// Session Replay - enabled
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
		// Only enable in non-local environments
		enabled: environment !== 'local',
		beforeSend(event: ErrorEvent, hint: EventHint) {
			const error = hint.originalException
			// Don't send auth-related errors (handled by app)
			if (error instanceof Error && error.message?.includes('401')) {
				return null
			}
			return event
		},
	})
}

// Export Sentry for user context updates
export { Sentry }
